import { memo } from 'react';

import Text from '@ui/text/text/text';
import Popover from '@ui/popover/popover';
import Button from '@ui/buttons/button/button';
import EditStateWrapper from '@ui/edit-state-wrapper/edit-state-wrapper';
import { TBroadcastTypePickerOptions } from './broadcast-type-picker.types';
import { EBroadcastTypes } from '@/types/session.types';

import { useAppSelector } from '@/store/hooks';
import { selectSaveId } from '@/store/slices/project/selectors';
import useGetColorForProjectThemeComponents from '@/utils/colors/use-get-color-for-project-theme-components';
import { EColorVariablesV2 } from '@/types/theme-packs.types';
import getBroadcastTypeLabel from './get-broadcast-type-label';
import getBroadcastTypeIcon from './get-broadcast-type-icon';
import { BROADCAST_TYPE_OPTIONS } from './broadcast-type-options';
import Icon from '@ui/icons/icon';
import { ICONS } from '@ui/icons/types';
import RotatableIcon from '@ui/RotatableIcon/rotatable-icon';
import styles from './broadcast-type-picker.module.css';
import Container from '@ui/container/container';

type BroadcastTypePickerProps = {
  disabled?: boolean;
  selected?: TBroadcastTypePickerOptions;
  onChange?: (broadcastType: TBroadcastTypePickerOptions) => void;
  onToggleOpen?: (isOpen: boolean) => void;
};

const BroadcastTypePicker = memo(({ disabled, selected, onChange, onToggleOpen }: BroadcastTypePickerProps) => {
  const saveId = useAppSelector(selectSaveId);
  const isUpdatingBroadcastType = saveId === 'broadcast-type-change';

  const { hex } = useGetColorForProjectThemeComponents({
    colorVariable: EColorVariablesV2.accents,
    contrastAgainstAppBackground: true,
  });

  const currentOption = selected ?? EBroadcastTypes.greenroom;

  return (
    <EditStateWrapper
      Label="SOURCE"
      className="position-relative cursor-pointer"
      headerClassName="position-absolute width-100-percent"
      disabled={disabled}
    >
      {({ isFocusedWithin }) => {
        const pill = (
          <Container
            className="flex ai-center gap-8 padding-h-12 height-28"
            border="2px solid var(--local-chip-input-default-outline)"
            borderRadius="100px"
            background="var(--local-chip-input-default-bg)"
            hoverBackground="var(--local-chip-input-hover-bg)"
            width="fit-content"
          >
            {!isFocusedWithin && getBroadcastTypeIcon(currentOption, 16)}
            <Text design="GlobalIcoText1" className="S3" whiteSpace="nowrap">
              {getBroadcastTypeLabel(currentOption)}
            </Text>
          </Container>
        );

        const optionList = BROADCAST_TYPE_OPTIONS.map((type) => (
          <Button
            key={type}
            backgroundColor="var(--global-cont-2)"
            hoverBackgroundColor="var(--global-cont-3)"
            outlineOnFocus={false}
            className="flex ai-center gap-10 radius-0 width-100-percent padding-v-8 padding-h-12 text-left"
            onClick={() => onChange?.(type)}
          >
            <div className="min-width-16 flex ai-center jc-center">{getBroadcastTypeIcon(type, 16)}</div>
            <Text className="L2" design="GlobalIcoText1" whiteSpace="nowrap">
              {getBroadcastTypeLabel(type)}
            </Text>
            {type === currentOption && (
              <div className="flex ai-center jc-flex-end flex-1">
                <Icon name={ICONS.CHECKMARK} size={12} color="var(--global-ico-text-3)" />
              </div>
            )}
          </Button>
        ));

        return (
          <Container className="margin-t-24 width-100-percent flex ai-center" style={{ height: '28px' }}>
            <div data-show-edit-label={isFocusedWithin ? 'false' : 'true'} className="width-100-percent">
              <Popover
                closeOnClick
                cycleWithArrowKeys
                backgroundColor="var(--global-cont-3)"
                radius="12px"
                inheritRadius
                border="none"
                width="100%"
                popoverWidth="100%"
                containerClassName="width-100-percent margin-t--2"
                contentClassName="margin-t-4"
                disabled={disabled || isUpdatingBroadcastType}
                renderTriggerButton={({ open }) =>
                  isFocusedWithin ? (
                    <Button
                      backgroundColor="var(--global-cont-1)"
                      hoverBackgroundColor="var(--global-cont-2)"
                      disabledOpacity={0.6}
                      border="1px solid var(--global-ico-text-1)"
                      disabled={isUpdatingBroadcastType}
                      className={`flex ai-center jc-space-between padding-h-8 keep-opacity-when-disabled ${styles.selectCard}`}
                      borderRadius="100px"
                      padding="4px"
                      onClick={() => onToggleOpen?.(open)}
                      hoverChildrenColor={hex}
                      data-open={open}
                      width="100%"
                      height="36px"
                    >
                      {pill}
                      <RotatableIcon
                        name={ICONS.SELECT_DROPDOWN_CARET}
                        size={16}
                        color="var(--global-ico-text-3)"
                        rotate={open ? '180deg' : '0deg'}
                      />
                    </Button>
                  ) : (
                    <Button className="clear-button" width="100%">
                      {pill}
                    </Button>
                  )
                }
              >
                {optionList}
              </Popover>
            </div>
          </Container>
        );
      }}
    </EditStateWrapper>
  );
});

export default BroadcastTypePicker;
